import { ButtonToggle } from 'eone-ui/buttons/button-toggle';
import { FormCheckbox } from 'eone-ui/forms/form-checkbox';
import { getDataTestId } from 'eone-ui/functions/data-test-ids';
import { SearchInput } from 'eone-ui/inputs/search-input';
import { useMemo, useState } from 'react';
import './index.css';

export type SCSelectListBadgeStyle = 'primary' | 'secondary' | 'danger' | 'warning' | 'default';

export interface SCSelectListItem {
  Id: string;
  Title: string;
  /** Second line, in monospace (an ID or code). */
  Subtitle?: string;
  Badges?: { Label: string; Style: SCSelectListBadgeStyle }[];
}

export interface SCSelectListProps {
  Title: string;
  /** The items after the caller's own filters; the search box filters them on Title and Subtitle. */
  Items: SCSelectListItem[];
  /** Shown as "{shown} of {Total}"; defaults to the number of Items. */
  Total?: number;
  SelectedId?: string;
  OnSelect: (id: string) => void;
  SearchPlaceholder?: string;
  FilterOptions?: { Value: string; Label: string }[];
  FilterValue?: string;
  OnFilter?: (value: string) => void;
  CheckboxLabel?: string;
  CheckboxValue?: boolean;
  OnToggleCheckbox?: (value: boolean) => void;
  EmptyMessage?: string;
  DataTestId?: string;
}

/** A searchable, filterable list to pick one item from, beside an editor for the selected item. */
export function SCSelectList(props: SCSelectListProps) {
  const [search, setSearch] = useState('');
  const shown = useMemo(() => {
    const text = search.trim().toLowerCase();
    return text
      ? props.Items.filter(
          (item) =>
            item.Title.toLowerCase().includes(text) ||
            !!item.Subtitle?.toLowerCase().includes(text),
        )
      : props.Items;
  }, [props.Items, search]);

  return (
    <div className="sc-select-list" data-testid={props.DataTestId}>
      <div className="sc-select-list-toolbar">
        <div className="sc-select-list-heading">
          <span className="sc-select-list-title">{props.Title}</span>
          <span className="sc-select-list-count">
            {shown.length} of {props.Total ?? props.Items.length}
          </span>
        </div>
        <SearchInput
          Placeholder={props.SearchPlaceholder}
          Value={search}
          OnChange={setSearch}
          DataTestId={getDataTestId(props.DataTestId, 'search')}
        />
        {props.FilterOptions && (
          <ButtonToggle
            Options={props.FilterOptions}
            Value={props.FilterValue}
            OnChange={props.OnFilter}
            DataTestId={getDataTestId(props.DataTestId, 'filter')}
          />
        )}
        {props.CheckboxLabel && (
          <FormCheckbox
            Inline
            NoPadding
            Label={props.CheckboxLabel}
            Value={props.CheckboxValue}
            OnToggle={props.OnToggleCheckbox}
            DataTestId={getDataTestId(props.DataTestId, 'checkbox')}
          />
        )}
      </div>
      <div className="sc-select-list-items" role="listbox">
        {shown.length === 0 && (
          <div className="sc-select-list-empty">{props.EmptyMessage ?? 'Nothing to show'}</div>
        )}
        {shown.map((item) => (
          <div
            key={item.Id}
            role="option"
            tabIndex={0}
            aria-selected={item.Id === props.SelectedId}
            className={
              item.Id === props.SelectedId
                ? 'sc-select-list-item sc-select-list-item-selected'
                : 'sc-select-list-item'
            }
            onClick={() => props.OnSelect(item.Id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                props.OnSelect(item.Id);
              }
            }}
            data-testid={getDataTestId(props.DataTestId, item.Id)}
          >
            <div className="sc-select-list-item-text">
              <span className="sc-select-list-item-title" title={item.Title}>
                {item.Title}
              </span>
              {item.Subtitle && (
                <span className="sc-select-list-item-subtitle" title={item.Subtitle}>
                  {item.Subtitle}
                </span>
              )}
            </div>
            {item.Badges?.map((badge) => (
              <span
                key={badge.Label}
                className={`eone-ui-card-badge eone-ui-card-badge-${badge.Style}`}
              >
                <span className="eone-ui-card-badge-label">{badge.Label}</span>
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
